import { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { useAppContext } from '../contexts/AppContext.tsx';

/**
 * 일계표 시스템 성능 최적화를 위한 커스텀 훅
 */
export const useDailyReportOptimization = (currentDate: string) => {
  const { employees } = useAppContext(); // budgetData 제거 - 사용되지 않음
  const dataCache = useRef(new Map());
  const [isOptimizing, setIsOptimizing] = useState(false);
  
  // 월별 데이터 키 생성 (메모이제이션)
  const monthKey = useMemo(() => currentDate.substring(0, 7), [currentDate]);
  
  // 직원 데이터 최적화 (팀별 그룹화)
  const employeesByTeam = useMemo(() => {
    const grouped = new Map<string, typeof employees>();
    employees.forEach(emp => {
      const team = emp.team || '미지정';
      if (!grouped.has(team)) {
        grouped.set(team, []);
      }
      grouped.get(team)!.push(emp);
    });
    return grouped;
  }, [employees]);
  
  // 데이터 변경 감지 (얕은 비교)
  const isDataChanged = useCallback((newData: any, cacheKey: string) => {
    const cached = dataCache.current.get(cacheKey);
    if (!cached) return true;
    
    // 간단한 직렬화 비교 (성능 개선을 위해 필요한 경우 더 정교한 비교 로직 사용)
    return JSON.stringify(newData) !== JSON.stringify(cached);
  }, []);
  
  // 계산 결과 캐싱
  const cacheResult = useCallback((key: string, data: any) => {
    dataCache.current.set(key, data);
  }, []);
  
  // 캐시된 결과 조회
  const getCachedResult = useCallback((key: string) => {
    return dataCache.current.get(key);
  }, []);
  
  // 캐시 무효화
  const invalidateCache = useCallback((pattern?: string) => {
    if (pattern) {
      const keys = Array.from(dataCache.current.keys());
      keys.forEach(key => {
        if (key.includes(pattern)) {
          dataCache.current.delete(key);
        }
      });
    } else {
      dataCache.current.clear();
    }
  }, []);
  
  // 날짜 변경 시 관련 캐시만 무효화
  useEffect(() => {
    invalidateCache(monthKey);
  }, [monthKey, invalidateCache]);
  
  // 배치 계산 최적화
  const batchCalculate = useCallback(async (calculations: Array<() => Promise<any>>) => {
    setIsOptimizing(true);
    try {
      // 계산들을 병렬로 실행
      const results = await Promise.allSettled(calculations.map(calc => calc()));
      return results.map(result => 
        result.status === 'fulfilled' ? result.value : null
      );
    } finally {
      setIsOptimizing(false);
    }
  }, []);
  
  return {
    monthKey,
    employeesByTeam,
    isDataChanged,
    cacheResult,
    getCachedResult,
    invalidateCache,
    batchCalculate,
    isOptimizing
  };
};

/**
 * 팀별 수납 데이터 최적화 훅
 */
export const useTeamCollectionOptimization = (assistData: any[], currentDate: string) => {
  // 현재 날짜 데이터만 필터링 (메모이제이션)
  const dailyData = useMemo(() => 
    assistData.filter(item => item.receiptDate === currentDate),
    [assistData, currentDate]
  );
  
  // 어시스트별 그룹화 (메모이제이션)
  const groupedByAssist = useMemo(() => {
    const grouped = new Map<string, typeof dailyData>();
    dailyData.forEach(item => {
      const assist = item.assistName || '미지정';
      if (!grouped.has(assist)) {
        grouped.set(assist, []);
      }
      grouped.get(assist)!.push(item);
    });
    return grouped;
  }, [dailyData]);
  
  // 팀별 합계 계산 (메모이제이션)
  const teamTotals = useMemo(() => {
    const totals = new Map<string, { count: number; amount: number }>();
    
    groupedByAssist.forEach((items, assist) => {
      const count = items.length;
      const amount = items.reduce((sum, item) => sum + (item.amount || 0), 0);
      
      // 팀 정보는 별도로 매핑 필요 (현재는 어시스트명만 있음)
      const team = '미지정'; // 실제로는 어시스트-팀 매핑 로직 필요
      
      if (!totals.has(team)) {
        totals.set(team, { count: 0, amount: 0 });
      }
      
      const current = totals.get(team)!;
      current.count += count;
      current.amount += amount;
    });
    
    return totals;
  }, [groupedByAssist]);
  
  return {
    dailyData,
    groupedByAssist,
    teamTotals
  };
}; 